/**
 * PublicHomePage — Landing page for unauthenticated visitors.
 *
 * Dashboard-style hero card + two big "Browse" cards (Rodeos, Jackpots).
 * Sits at "/" via App.tsx routing for visitors without a session. Signed-in
 * users at "/" get the real DashboardPage instead.
 */

import { Link } from 'react-router-dom';
import { Trophy, Calendar, ArrowRight } from 'lucide-react';

export default function PublicHomePage() {
  return (
    <div className="space-y-6">
      {/* Welcome hero */}
      <div className="rounded-xl border overflow-hidden">
        <div className="h-1 bg-primary" />
        <div className="p-6">
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome to HighCall Rodeo
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse upcoming rodeos and jackpots. Sign in to register or manage
            your entries.
          </p>
        </div>
      </div>

      {/* Browse cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Link
          to="/rodeos"
          className="group rounded-xl border bg-card p-6 transition-colors hover:border-primary"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Calendar className="h-6 w-6 text-primary" />
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-1">
            Browse Rodeos
          </h2>
          <p className="text-sm text-muted-foreground">
            Multi-day rodeo events with team roping, breakaway, tie-down, and more.
          </p>
        </Link>

        <Link
          to="/jackpots"
          className="group rounded-xl border bg-card p-6 transition-colors hover:border-primary"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Trophy className="h-6 w-6 text-primary" />
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-1">
            Browse Jackpots
          </h2>
          <p className="text-sm text-muted-foreground">
            Single-day and weekly jackpot ropings with cash prizes.
          </p>
        </Link>
      </div>

      {/* Sign-in CTA card */}
      <div className="rounded-xl border bg-card p-6 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Already a member? Sign in to register for events, manage your buddy
          groups, and track your standings.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
          >
            Sign In
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Create Account
          </Link>
        </div>
      </div>
    </div>
  );
}
